import { Vector3 } from 'three';
import { captureHighResFrame } from './canvasCapture';
import { scaleBarOverlaySpec } from '../components/ui/ScaleBar';
import {
  formatMine,
  resolvedSectionPose,
  sectionAxisLabel,
  sectionFilename,
} from './sectionMath';

const DRAW_CANVAS_ID = 'mdv-draw-canvas';

function fillBackdrop(ctx, w, h) {
  ctx.fillStyle = '#141414';
  ctx.fillRect(0, 0, w, h);
}

function compositeDrawOverlay(ctx, destW, destH) {
  const overlay = document.getElementById(DRAW_CANVAS_ID);
  if (!overlay || overlay.width < 1 || overlay.height < 1) return;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(overlay, 0, 0, destW, destH);
}

function northRotationDeg(camera) {
  if (!camera || typeof camera.getWorldDirection !== 'function') return 0;
  const dir = camera.getWorldDirection(new Vector3());
  const len = Math.hypot(dir.x, dir.z) || 1;
  const angle = Math.atan2(dir.x / len, dir.z / len);
  let degrees = (angle * 180) / Math.PI;
  if (degrees < 0) degrees += 360;
  return (degrees + 180) % 360;
}

function drawNorth(ctx, x, y, deg, ui) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate((deg * Math.PI) / 180);
  ctx.fillStyle = '#eab308';
  ctx.beginPath();
  ctx.moveTo(0, -16 * ui);
  ctx.lineTo(7 * ui, 8 * ui);
  ctx.lineTo(0, 3 * ui);
  ctx.lineTo(-7 * ui, 8 * ui);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
  ctx.fillStyle = '#facc15';
  ctx.font = `bold ${11 * ui}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillText('N', x, y + 28 * ui);
  ctx.textAlign = 'start';
}

export function downloadSectionExport(gl, camera, controls, meta = {}) {
  const scene = meta.scene;
  const { canvas: captured, width: w, height: h, scale } = captureHighResFrame(gl, scene, camera, {
    scale: meta.scale,
  });

  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const ctx = out.getContext('2d');
  fillBackdrop(ctx, w, h);
  ctx.drawImage(captured, 0, 0, w, h);
  compositeDrawOverlay(ctx, w, h);

  const ui = scale || 1;
  const pad = 22 * ui;
  const state = meta.state || {};
  const pose = resolvedSectionPose(state);
  const site = meta.projectName || meta.title || 'Section';
  const axis = sectionAxisLabel(state);
  const widthM = Number(state.width);
  const when = new Date().toLocaleString();

  const boxW = Math.min(w - pad * 2, 420 * ui);
  const boxH = 72 * ui;
  const boxY = h - boxH - 18 * ui;
  ctx.fillStyle = 'rgba(0,0,0,0.78)';
  ctx.fillRect(pad - 8 * ui, boxY, boxW, boxH);
  ctx.strokeStyle = '#eab308';
  ctx.lineWidth = ui;
  ctx.strokeRect(pad - 8 * ui, boxY, boxW, boxH);

  ctx.fillStyle = '#facc15';
  ctx.font = `bold ${13 * ui}px sans-serif`;
  ctx.fillText(site, pad, boxY + 22 * ui);
  ctx.fillStyle = '#e5e5e5';
  ctx.font = `${11 * ui}px sans-serif`;
  const line2 = [
    axis,
    `E ${formatMine(pose.east, 1)}`,
    `RL ${formatMine(pose.rl, 1)}`,
    `N ${formatMine(pose.north, 1)}`,
    Number.isFinite(widthM) ? `${widthM} m` : '',
  ]
    .filter(Boolean)
    .join('  ·  ');
  ctx.fillText(line2, pad, boxY + 42 * ui);
  ctx.fillStyle = '#a1a1aa';
  ctx.font = `${10 * ui}px sans-serif`;
  ctx.fillText(when, pad, boxY + 60 * ui);

  const spec = scaleBarOverlaySpec(camera, gl?.domElement, controls?.target);
  if (spec) {
    const barW = spec.widthPx * ui;
    const barX = w - pad - barW - 8 * ui;
    const barY = h - 36 * ui;
    ctx.fillStyle = 'rgba(0,0,0,0.72)';
    ctx.fillRect(barX - 10 * ui, barY - 28 * ui, barW + 20 * ui, 40 * ui);
    ctx.fillStyle = '#eab308';
    ctx.fillRect(barX, barY - 6 * ui, barW, 3 * ui);
    ctx.fillStyle = '#facc15';
    ctx.font = `${10 * ui}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.fillText(spec.label, barX + barW / 2, barY - 12 * ui);
    ctx.textAlign = 'start';
  }

  drawNorth(ctx, w - pad - 18 * ui, pad + 22 * ui, northRotationDeg(camera), ui);

  const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  const name = meta.filename || sectionFilename(state, site) || `section-${stamp}.png`;

  return new Promise((resolve, reject) => {
    out.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Could not encode PNG'));
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 2000);
      resolve(name);
    }, 'image/png');
  });
}
